<template>
  <div class="time-drum-picker">
    <div
      ref="colEl"
      class="drum-col"
      @scroll="onScroll"
    >
      <div class="drum-spacer" />
      <div
        v-for="opt in options"
        :key="`opt-${opt.value}`"
        :class="['drum-cell', { 'is-selected': opt.value === selectedValue }]"
        :data-value="opt.value"
        @click="selectValue(opt.value)"
      >
        {{ opt.label }}
      </div>
      <div class="drum-spacer" />
    </div>
    <div class="drum-overlay" />
  </div>
</template>

<script>
import {
  ref, computed, onMounted, watch, nextTick,
} from '@vue/composition-api'

const pad2 = n => String(n).padStart(2, '0')
const CELL_HEIGHT = 56
const STEP_MIN = 5
const RANGE_MIN = 60 // ±1 時間
const STEPS_PER_SIDE = RANGE_MIN / STEP_MIN // 12

// Date → ステップ単位の分（その日 00:00 からの分数を STEP_MIN に丸めた値）
const toStepMinutes = date => {
  const m = date.getHours() * 60 + date.getMinutes()

  return Math.round(m / STEP_MIN) * STEP_MIN
}

// ステップ分を [0, 1440) に正規化
const wrapMin = m => ((m % 1440) + 1440) % 1440

const labelOf = stepMin => {
  const m = wrapMin(stepMin)

  return `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`
}

export default {
  name: 'TimeDrumPicker',
  props: {
    value: { type: Date, default: null },
  },
  emits: ['input'],
  setup(props, { emit }) {
    const colEl = ref(null)

    const baseDate = computed(() => (
      props.value instanceof Date ? props.value : new Date()
    ))

    // 選択中の値（その日の何分か。STEP_MIN 倍数）
    const selectedValue = ref(toStepMinutes(baseDate.value))

    // anchor = 「中央に置く時刻」 = 現在選択値
    const anchorValue = ref(selectedValue.value)

    // 表示候補: anchor ± RANGE_MIN を STEP_MIN 刻みで生成（日付跨ぎは wrap）
    const options = computed(() => {
      const out = []
      for (let i = -STEPS_PER_SIDE; i <= STEPS_PER_SIDE; i += 1) {
        const v = wrapMin(anchorValue.value + i * STEP_MIN)
        out.push({ value: v, label: labelOf(v) })
      }

      return out
    })

    const indexOfSelected = computed(() => (
      options.value.findIndex(o => o.value === selectedValue.value)
    ))

    const scrollToIndex = idx => {
      const el = colEl.value
      if (!el) return
      el.scrollTop = Math.max(0, idx) * CELL_HEIGHT
    }

    const syncScroll = async () => {
      await nextTick()
      const i = indexOfSelected.value >= 0
        ? indexOfSelected.value
        : STEPS_PER_SIDE
      scrollToIndex(i)
    }

    const buildEmitDate = stepMin => {
      const base = props.value instanceof Date
        ? new Date(props.value.getTime()) : new Date()
      const m = wrapMin(stepMin)
      base.setHours(Math.floor(m / 60), m % 60, 0, 0)

      return base
    }

    const emitChange = () => {
      emit('input', buildEmitDate(selectedValue.value))
    }

    let scrollTimer = null
    const onScroll = () => {
      if (scrollTimer) clearTimeout(scrollTimer)
      scrollTimer = setTimeout(() => {
        const el = colEl.value
        if (!el) return
        const idx = Math.round(el.scrollTop / CELL_HEIGHT)
        const clamped = Math.min(options.value.length - 1, Math.max(0, idx))
        const next = options.value[clamped]?.value
        if (next != null && next !== selectedValue.value) {
          selectedValue.value = next
          emitChange()
        }
        scrollToIndex(clamped)
      }, 120)
    }

    const selectValue = v => {
      if (v === selectedValue.value) return
      selectedValue.value = v
      emitChange()
      syncScroll()
    }

    watch(() => props.value, v => {
      if (!(v instanceof Date)) return
      const next = toStepMinutes(v)
      if (next !== selectedValue.value) {
        selectedValue.value = next
        anchorValue.value = next
        syncScroll()
      }
    })

    onMounted(() => {
      syncScroll()
    })

    return {
      colEl,
      options,
      selectedValue,
      onScroll,
      selectValue,
    }
  },
}
</script>

<style scoped>
.time-drum-picker {
  position: relative;
  display: inline-block;
  user-select: none;
}
.drum-col {
  width: 144px;
  height: 168px; /* CELL_HEIGHT * 3 */
  overflow-y: auto;
  scroll-snap-type: y mandatory;
  scrollbar-width: none;
  -ms-overflow-style: none;
  text-align: center;
  border: 1px solid rgba(127, 127, 127, 0.32);
  border-radius: 12px;
  background: transparent;
}
.drum-col::-webkit-scrollbar {
  display: none;
}
.drum-cell {
  height: 56px;
  line-height: 56px;
  font-size: 1.7rem;
  scroll-snap-align: center;
  cursor: pointer;
  color: currentColor;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
  transition: opacity 0.15s ease, color 0.15s ease;
}
.theme--light .drum-cell {
  opacity: 0.55;
}
.theme--dark .drum-cell {
  /* ダークでは白文字を見やすく */
  opacity: 0.75;
}
.drum-cell:hover {
  opacity: 0.95;
}
.drum-cell.is-selected {
  font-weight: 700;
  font-size: 1.9rem;
  opacity: 1;
  color: var(--v-primary-base);
}
.drum-spacer {
  height: 56px;
  scroll-snap-align: center;
}
.drum-overlay {
  pointer-events: none;
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  height: 56px;
  border-top: 1.5px solid rgba(98, 0, 234, 0.45);
  border-bottom: 1.5px solid rgba(98, 0, 234, 0.45);
  background: rgba(98, 0, 234, 0.04);
}
</style>
